import {uniqBy} from 'lodash';
import {MessageDescriptor} from 'react-intl';
import {getGroupFiltersSelected} from './filters';
import {
    PRICE_FILTER_GROUP_ID,
    ROOMSANDSPACES_FILTER_GROUP_ID,
    FREE_CANCELLATION_GROUP_ID,
} from '../constants/filters';
import {
    getLimitedMaxPrice,
    getLimitedMaxDisplayPrice,
    getFormattedCurrencyRange,
} from './priceUtils';
import {getRoomsAndSpacesLabel} from './roomsAndSpacesUtils';
import {getFiltersWithSelection} from './freeCancellationUtils';

import type {
    FilterBarProps,
    Refinement,
    FilterGroupType,
} from '../components/FilterBar/FilterBar';

export function findInGroupFilters(
    filterGroups: FilterGroupType[],
    groupId: string
): boolean {
    return !!filterGroups.find(({groupInfo}) => groupInfo.id === groupId);
}

type GetFilterGroupPillLabelParams = {
    filters: Refinement[];
    formatMessage: (
        messageDescriptor: MessageDescriptor,
        values?: Record<string, any>
    ) => string;
    label: string;
    locale: string;
    groupId?: string;
} & Pick<
    FilterBarProps,
    'appliedFilters' | 'customFiltersParams' | 'priceFilterOptions' | 'currency'
>;

// eslint-disable-next-line complexity
export function getFilterGroupPillLabel({
    appliedFilters,
    filters,
    groupId,
    label,
    locale,
    formatMessage,
    currency,
    customFiltersParams,
    priceFilterOptions = {},
}: GetFilterGroupPillLabelParams): string {
    const {
        appliedMinBathrooms,
        appliedMinBedrooms,
        appliedMinTotalPrice,
        appliedMinNightlyPrice,
        appliedMaxTotalPrice,
        appliedMaxNightlyPrice,
        selectedFilterByTotalPrice: filterByTotalPrice,
    } = customFiltersParams;

    if (groupId === PRICE_FILTER_GROUP_ID) {
        const actingMinPrice = filterByTotalPrice
            ? appliedMinTotalPrice
            : appliedMinNightlyPrice;
        const actingMaxPrice = filterByTotalPrice
            ? appliedMaxTotalPrice
            : appliedMaxNightlyPrice;

        const isPriceApplied = !!actingMinPrice || !!actingMaxPrice;
        if (isPriceApplied) {
            const maxAllowedPrice = getLimitedMaxPrice(
                currency,
                filterByTotalPrice,
                priceFilterOptions.lengthOfStay
            );
            const maxDisplayPrice = getLimitedMaxDisplayPrice(
                maxAllowedPrice,
                actingMaxPrice
            );
            const isMax = maxDisplayPrice >= maxAllowedPrice;

            return getFormattedCurrencyRange({
                minPrice: actingMinPrice || 0,
                maxPrice: maxDisplayPrice,
                currency,
                locale,
                isMax,
            });
        }
    } else if (groupId === ROOMSANDSPACES_FILTER_GROUP_ID) {
        const currentApplied = getGroupFiltersSelected(filters, appliedFilters);
        const isBathroomsApplied = !!appliedMinBathrooms;
        const isBedroomsApplied = !!appliedMinBedrooms;

        // Check if any bedrooms/bathrooms are applied and no other spaces is applied
        if (
            (isBathroomsApplied || isBedroomsApplied) &&
            !currentApplied.length
        ) {
            const appliedLabel = getRoomsAndSpacesLabel(
                appliedMinBedrooms,
                appliedMinBathrooms,
                formatMessage
            );

            /* istanbul ignore else */
            if (appliedLabel) {
                return appliedLabel;
            }
        }
    }

    return label;
}

type GetAppliedCountParams = {
    filterGroups?: FilterGroupType[];
    filters: Refinement[];
    groupId?: string;
} & Pick<FilterBarProps, 'appliedFilters' | 'customFiltersParams'>;

// eslint-disable-next-line complexity
export function getAppliedCount({
    appliedFilters,
    filters,
    filterGroups = [],
    groupId,
    customFiltersParams,
}: GetAppliedCountParams): number {
    const {
        appliedMinNightlyPrice,
        appliedMinTotalPrice,
        appliedMaxNightlyPrice,
        appliedMaxTotalPrice,
        appliedMinBathrooms,
        appliedMinBedrooms,
    } = customFiltersParams;

    const currentAppliedUnique = uniqBy(
        getGroupFiltersSelected(filters, appliedFilters),
        (filter) => filter.filter.refineByQueryArgument
    );
    const isPriceApplied = [
        appliedMinNightlyPrice,
        appliedMinTotalPrice,
        appliedMaxNightlyPrice,
        appliedMaxTotalPrice,
    ].some((val) => !!val);
    const isBathroomsApplied = !!appliedMinBathrooms;
    const isBedroomsApplied = !!appliedMinBedrooms;

    // No groupId means it's calculating the total count of applied filters of a group of filters - like More filters pill
    if (!groupId && filterGroups.length) {
        let count = currentAppliedUnique.length;

        // Check if Price group is part of the filter groups
        if (findInGroupFilters(filterGroups, PRICE_FILTER_GROUP_ID)) {
            count += isPriceApplied ? 1 : 0;
        }

        // Check if Rooms and Spaces group is part of the filter groups
        if (findInGroupFilters(filterGroups, ROOMSANDSPACES_FILTER_GROUP_ID)) {
            count += isBedroomsApplied ? 1 : 0;
            count += isBathroomsApplied ? 1 : 0;
        }

        return count;
    }

    // Specific logic for Rooms and Spaces count based on minBedrooms/minBathrooms applied
    if (groupId === ROOMSANDSPACES_FILTER_GROUP_ID) {
        // Check if any bedrooms/bathrooms are applied and no other spaces is applied
        if (
            (isBathroomsApplied || isBedroomsApplied) &&
            !currentAppliedUnique.length
        ) {
            return 0;
        }

        let count = currentAppliedUnique.length;
        count += isBedroomsApplied ? 1 : 0;
        count += isBathroomsApplied ? 1 : 0;

        return count;
    }

    // Specific logic for Free cancellation count based policy selected
    if (groupId === FREE_CANCELLATION_GROUP_ID) {
        return getFiltersWithSelection(filters, appliedFilters).filter(
            (item) => item.isChecked
        ).length;
    }

    // Default filter group count
    return currentAppliedUnique.length;
}
